/**
 * The app-wide email notification stream: ONE SSE connection per tab, owned by
 * the authenticated shell, open while the document is visible.
 *
 * Uses api.fetchRaw() with ReadableStream instead of EventSource because
 * EventSource doesn't support custom Authorization headers.
 *
 * Each 'email_updated' event is published to `emailUpdates` subscribers (the
 * inbox list, the email page) and bumps the Zustand `emailRefreshTrigger`
 * (debounced) so the nav badges refetch. Every successful (re)open is
 * published too, so subscribers can catch up on what they missed while it was
 * down. The connection also drives the
 * app-wide `sseConnected` flag, which sets every poller's fallback cadence —
 * so it must be live on every view, not only where the inbox is mounted.
 */
import { useEffect } from 'react';

import { useAppStore } from '@/app/stores/useAppStore';
import { api } from '@/shared/services/api';
import { parseSSEEvents, type SSEEvent } from '@/shared/utils/sseParser';

import {
  createDebounceGate,
  EMAIL_UPDATE_DEBOUNCE_MS,
  publishEmailUpdate,
  publishStreamOpened,
} from './emailUpdates';

const SSE_URL = '/api/email/notifications/stream';
const RECONNECT_DELAY_MS = 5000;
/** Retry ceiling. The stream never gives up: a tab left open must recover on its own. */
export const MAX_RECONNECT_DELAY_MS = 60_000;
/** This stream's key in the store's connection set; there is exactly one. */
const STREAM_CONNECTION_ID = 'app-email-notification-stream';

/**
 * Extract the email_id from an email_updated SSE event's JSON data.
 * Returns null when the payload is empty, malformed, has no numeric id,
 * or carries a `brief_summary`/`system` category -- those categories
 * identify InboxItem ids (which can numerically collide with email ids)
 * rather than ProcessedEmail ids, so a per-email refetch would be wrong.
 */
export function parseEmailId(data: string): number | null {
  if (!data) return null;
  try {
    const parsed: unknown = JSON.parse(data);
    const category = (parsed as { category?: unknown }).category;
    if (category === 'brief_summary' || category === 'system') return null;
    const id = (parsed as { email_id?: unknown }).email_id;
    return typeof id === 'number' ? id : null;
  } catch {
    return null;
  }
}

/** Delay before reconnect attempt `attempt` (1-based): 5s doubling, capped at 60s. */
export function reconnectDelayMs(attempt: number): number {
  return Math.min(RECONNECT_DELAY_MS * 2 ** (attempt - 1), MAX_RECONNECT_DELAY_MS);
}

/** What one stream reports to the app. */
interface StreamCallbacks {
  setConnected: (connected: boolean) => void;
  /** Called once per successful (re)open, after `setConnected(true)`. */
  onOpened: () => void;
  onEvent: (event: SSEEvent) => void;
}

/** Read the body until it ends, dispatching each parsed SSE event. */
async function readEvents(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: SSEEvent) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const { events, remaining } = parseSSEEvents(buffer);
    buffer = remaining;
    events.forEach(onEvent);
  }
}

/**
 * Run one connection to completion. Resolves when the stream ends, fails, or
 * is aborted; reports disconnected only if it was not deliberately aborted
 * (whoever aborted has already reported, and a newer connection may be live).
 */
async function runConnection(
  abort: AbortController,
  { setConnected, onEvent }: StreamCallbacks,
  onOpen: () => void
): Promise<void> {
  try {
    const response = await api.fetchRaw(SSE_URL, {
      headers: { Accept: 'text/event-stream' },
      signal: abort.signal,
    });
    if (!response.body) throw new Error('SSE connection returned no body');
    setConnected(true);
    onOpen();
    await readEvents(response.body, onEvent);
  } catch (error) {
    if ((error as Error).name !== 'AbortError') console.warn('SSE connection error:', error);
  }
  if (!abort.signal.aborted) setConnected(false);
}

/**
 * A restartable stream that reconnects into itself with capped exponential
 * back-off until stopped — it never gives up, because a tab left open (an
 * installed PWA) has no remount to rescue it. `stop` also cancels a pending
 * retry, so nothing reconnects behind a hidden tab.
 */
function createEmailStream(callbacks: StreamCallbacks): { start: () => void; stop: () => void } {
  let controller: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;

  const open = (): void => {
    const own = new AbortController();
    controller = own;
    const onOpen = (): void => {
      attempts = 0;
      callbacks.onOpened();
    };
    void runConnection(own, callbacks, onOpen).then(() => {
      if (own.signal.aborted) return;
      attempts++;
      retryTimer = setTimeout(open, reconnectDelayMs(attempts));
    });
  };

  const stop = (): void => {
    controller?.abort();
    controller = null;
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
    callbacks.setConnected(false);
  };

  const start = (): void => {
    stop();
    attempts = 0;
    open();
  };

  return { start, stop };
}

/**
 * Open the app-wide email notification stream. Mount ONCE, in the
 * authenticated shell: views subscribe through `useEmailUpdates` rather than
 * opening their own connection.
 *
 * Open while the document is visible; closed (any pending retry cancelled)
 * while hidden, and reopened with a fresh back-off when shown again.
 */
export function useEmailNotificationStream(): void {
  const triggerEmailRefresh = useAppStore(state => state.triggerEmailRefresh);
  const reportConnected = useAppStore(state => state.setSSEConnected);

  useEffect(() => {
    const refreshGate = createDebounceGate(EMAIL_UPDATE_DEBOUNCE_MS);
    const stream = createEmailStream({
      setConnected: connected => reportConnected(STREAM_CONNECTION_ID, connected),
      onOpened: publishStreamOpened,
      onEvent: event => {
        if (event.eventType !== 'email_updated') return;
        publishEmailUpdate(parseEmailId(event.data));
        if (refreshGate()) triggerEmailRefresh();
      },
    });
    const sync = (): void => {
      if (document.visibilityState === 'hidden') stream.stop();
      else stream.start();
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      stream.stop();
    };
  }, [triggerEmailRefresh, reportConnected]);
}
